import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import { getNotifications } from "../../helpers/notification_helper";
import { useNotifications } from "../../contexts/NotificationContext";
import "./Notifications.scss";
import { parseUtcDateTime } from "../../helpers/utc-date";

const categories = [
  "All",
  "Tasks",
  "Reviews",
  "Imports",
  "Queries",
  "Findings",
  "System",
];

const iconFor = (item) =>
  item.notificationType?.includes("Import")
    ? "upload-cloud"
    : item.notificationType?.includes("Review")
    ? "file-text"
    : item.notificationType?.includes("Query")
    ? "message-circle"
    : item.notificationType?.includes("Finding")
    ? "flag"
    : "check-square";

const dayKey = (date) => {
  const value = parseUtcDateTime(date);
  const today = new Date();
  const yesterday = new Date(Date.now() - 86400000);
  return value.toDateString() === today.toDateString()
    ? "Today"
    : value.toDateString() === yesterday.toDateString()
    ? "Yesterday"
    : new Intl.DateTimeFormat(undefined, {
        weekday: "long",
        month: "short",
        day: "numeric",
      }).format(value);
};

const NotificationsPage = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState("all");
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const { markRead, markAllRead } = useNotifications();
  const navigate = useNavigate();
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getNotifications({
        page,
        pageSize: 25,
        state,
        category,
        search,
      });
      if (response?.status) {
        setItems(response.data?.items || []);
        setTotal(response.data?.totalCount || 0);
      }
    } finally {
      setLoading(false);
    }
  }, [page, state, category, search]);
  useEffect(() => {
    load();
  }, [load]);
  const groups = useMemo(
    () =>
      items.reduce((all, item) => {
        const key = dayKey(item.createdAt);
        (all[key] ||= []).push(item);
        return all;
      }, {}),
    [items]
  );

  const open = async (item) => {
    if (!item.isRead) {
      await markRead(item.id);
      setItems((all) =>
        all.map((value) =>
          value.id === item.id ? { ...value, isRead: true } : value
        )
      );
    }
    navigate(item.actionUrl || "/notifications");
  };

  const markOne = async (item) => {
    if (!item.isRead) {
      await markRead(item.id);
      setItems((all) =>
        all.map((value) =>
          value.id === item.id ? { ...value, isRead: true } : value
        )
      );
    }
  };

  const markAll = async () => {
    await markAllRead();
    setItems((all) => all.map((item) => ({ ...item, isRead: true })));
  };
  return (
    <div className="page-content notification-page piers-page">
      <div className="container-fluid">
        <div className="notification-page__header piers-page-header">
          <div>
            <h2 className="piers-page-title">Notifications</h2>
            <p>
              Review updates, assignments and activity requiring your attention.
            </p>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-light" onClick={markAll}>
              <FeatherIcon icon="check" size={16} /> Mark all as read
            </button>
            <button
              className="btn btn-primary"
              onClick={() => navigate("/notifications/settings")}
            >
              <FeatherIcon icon="settings" size={16} /> Notification settings
            </button>
          </div>
        </div>
        <div className="notification-page__filters">
          <div className="input-group">
            <span className="input-group-text">
              <FeatherIcon icon="search" size={16} />
            </span>
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="form-control"
              placeholder="Search notifications..."
            />
          </div>
          <div className="btn-group">
            {["all", "unread", "read"].map((value) => (
              <button
                key={value}
                className={`btn btn-sm ${
                  state === value ? "btn-primary" : "btn-outline-secondary"
                }`}
                onClick={() => {
                  setState(value);
                  setPage(1);
                }}
              >
                {value[0].toUpperCase() + value.slice(1)}
              </button>
            ))}
          </div>
          <select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setPage(1);
            }}
            className="form-select"
          >
            {categories.map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </div>
        {loading ? (
          <div className="notification-page__empty">Loading notifications…</div>
        ) : items.length === 0 ? (
          <div className="notification-page__empty">
            <FeatherIcon icon="check-circle" size={38} />
            <h5>You’re all caught up</h5>
            <p>No notifications match these filters.</p>
          </div>
        ) : (
          Object.entries(groups).map(([day, values]) => (
            <section key={day} className="notification-day">
              <h5>{day}</h5>
              {values.map((item) => (
                <article
                  key={item.id}
                  className={`notification-row ${
                    !item.isRead ? "notification-row--unread" : ""
                  }`}
                >
                  <span className="notification-row__icon">
                    <FeatherIcon icon={iconFor(item)} size={19} />
                  </span>
                  <button
                    type="button"
                    className="notification-row__body"
                    onClick={() => open(item)}
                  >
                    <strong>{item.title}</strong>
                    <span>{item.message}</span>
                    <small>
                      {new Intl.DateTimeFormat(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      }).format(parseUtcDateTime(item.createdAt))}
                    </small>
                  </button>
                  <div className="notification-row__action">
                    {!item.isRead && (
                      <button
                        className="btn btn-sm btn-link"
                        onClick={() => markOne(item)}
                      >
                        Mark as read
                      </button>
                    )}
                    {item.actionLabel && (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => open(item)}
                      >
                        {item.actionLabel}
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </section>
          ))
        )}
        {total > 25 && (
          <div className="d-flex justify-content-center gap-2 mt-3">
            <button
              className="btn btn-outline-secondary"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>
            <span className="align-self-center">Page {page}</span>
            <button
              className="btn btn-outline-secondary"
              disabled={page * 25 >= total}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
export default NotificationsPage;
